import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Smartphone, Download } from "lucide-react";
import { detectDevice, getBrowserName } from "@/lib/deviceDetect";

function StepList({ steps }) {
  return (
    <ol className="space-y-2 list-decimal list-inside text-sm text-slate-600">
      {steps.map((step, i) => (
        <li key={i}>{step}</li>
      ))}
    </ol>
  );
}

function XiaomiSteps() {
  const browser = getBrowserName();

  if (browser === "mi-browser") {
    return (
      <StepList
        steps={[
          <>Ouvrez le menu <strong>≡</strong> (en bas de l'écran)</>,
          <>Appuyez sur <strong>« Ajouter à l'écran d'accueil »</strong></>,
          <>Confirmez avec <strong>« Ajouter »</strong></>,
          <>L'icône Coach apparaît sur votre écran d'accueil</>,
        ]}
      />
    );
  }

  return (
    <StepList
      steps={[
        <>Ouvrez <strong>Chrome</strong> (recommandé sur Xiaomi)</>,
        <>Menu <strong>⋮</strong> (3 points, en haut à droite)</>,
        <><strong>« Ajouter à l'écran d'accueil »</strong> ou <strong>« Installer l'application »</strong></>,
        <>Si absent : <strong>⋮</strong> → <strong>Partager</strong> → <strong>« Ajouter à l'écran d'accueil »</strong></>,
        <>Appuyez sur <strong>« Ajouter »</strong></>,
      ]}
    />
  );
}

function SamsungSteps() {
  const browser = getBrowserName();

  if (browser === "samsung-browser") {
    return (
      <StepList
        steps={[
          <>Menu <strong>☰</strong> (barre du bas)</>,
          <><strong>« Ajouter page à »</strong> → <strong>« Écran d'accueil »</strong></>,
          <>Confirmez avec <strong>« Ajouter »</strong></>,
        ]}
      />
    );
  }

  return (
    <StepList
      steps={[
        <>Menu <strong>⋮</strong> (3 points en haut à droite)</>,
        <><strong>« Ajouter à l'écran d'accueil »</strong> ou <strong>« Installer l'application »</strong></>,
        <>Confirmez avec <strong>« Installer »</strong></>,
      ]}
    />
  );
}

function IosSteps() {
  return (
    <StepList
      steps={[
        <>Bouton <strong>Partager</strong> (carré avec flèche vers le haut)</>,
        <><strong>« Sur l'écran d'accueil »</strong></>,
        <><strong>« Ajouter »</strong> en haut à droite</>,
      ]}
    />
  );
}

function AndroidSteps() {
  return (
    <StepList
      steps={[
        <>Menu <strong>⋮</strong> du navigateur</>,
        <><strong>« Ajouter à l'écran d'accueil »</strong> ou <strong>« Installer l'application »</strong></>,
        <>Confirmez l'installation</>,
      ]}
    />
  );
}

const ANDROID_TABS = [
  { key: "xiaomi", label: "Xiaomi", Steps: XiaomiSteps },
  { key: "samsung", label: "Samsung", Steps: SamsungSteps },
  { key: "other", label: "Autre", Steps: AndroidSteps },
];

export function PwaInstallGuide({ open, onOpenChange }) {
  const device = detectDevice();
  const [androidTab, setAndroidTab] = useState(
    device === "samsung" ? "samsung" : "xiaomi"
  );

  const renderSteps = () => {
    if (device === "ios") return <IosSteps />;
    if (device === "xiaomi") return <XiaomiSteps />;
    if (device === "samsung") return <SamsungSteps />;

    const tab = ANDROID_TABS.find((t) => t.key === androidTab) || ANDROID_TABS[0];
    const { Steps } = tab;

    return (
      <>
        <div className="flex gap-1 mb-3 p-1 bg-slate-100 rounded-lg">
          {ANDROID_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setAndroidTab(t.key)}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${
                androidTab === t.key
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <Steps />
      </>
    );
  };

  const title =
    device === "ios"
      ? "iPhone / iPad"
      : device === "samsung"
        ? "Samsung"
        : device === "xiaomi"
          ? "Xiaomi / Redmi / POCO"
          : "Android";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-600" />
            Installer Coach sur votre téléphone
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-xl p-4 bg-blue-50">
            <div className="flex items-center gap-2 mb-1">
              <Smartphone className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-600">{title}</p>
            </div>
            <p className="text-xs text-slate-600">
              Sur certains téléphones (Xiaomi, etc.), l'installation se fait via le menu du navigateur — pas via un bouton automatique.
            </p>
          </div>

          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">
              Étapes à suivre
            </p>
            {renderSteps()}
          </div>

          <p className="text-xs text-slate-400 text-center">
            Une fois installée, Coach s'ouvre en plein écran, comme une vraie application.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}